import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { debounce, isEmpty, keyBy } from 'lodash';

import CardDialog from '~/components/CardDialog';
import { useClaim } from '~/components/ClaimContainer';
import type { ClaimNotificationModel } from '~/components/ClaimNotificationsCard/types';
import CounterWithRefresh from '~/components/core/CounterWithRefresh/CounterWithRefresh';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import ToggleButtonGroup from '~/components/core/Molecules/Fields/ToggleButtonGroup';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { useCms } from '~/components/hooks/useCms';
import useNotificationsPreferences from '~/components/hooks/useNotificationsPreferences';
import { DismissIcon, MailIcon } from '~/components/icons';
import OpenMailIcon from '~/components/icons/OpenMailIcon';
import BulkSelectionPanel from '~/components/Notifications/BulkSelectionPanel';
import { useNotificationsPagination } from '~/components/Notifications/hooks/useNotificationsPagination';
import NotificationCountersView from '~/components/Notifications/Kanban/NotificationCountersView/NotificationCountersView';
import NotificationKanbanBoard from '~/components/Notifications/Kanban/NotificationKanbanBoard/NotificationKanbanBoard';
import type { NotificationsFiltersValues } from '~/components/Notifications/NotificationsFilters';
import NotificationsFilters from '~/components/Notifications/NotificationsFilters';
import NotificationsTable from '~/components/Notifications/NotificationsTable';
import type { NotificationsTableDisplayOptions } from '~/components/Notifications/types';
import ViewNotificationCard from '~/components/Notifications/ViewNotificationCard';
import type { NotificationsPreferences } from '~/components/SystemConfiguration/NotificationsConfiguration/types';
import type { ExposureModel } from '~/components/types/exposure-types';
import { localDateToEndOfDayUTC, localToServerDateTimeNoonUTC, serverDateToLocal } from '~/DateTimeUtils';
import type CLAIM_NOTIFICATIONS_PRIORITY from '~/server_shared/generated-types/CLAIM_NOTIFICATIONS_PRIORITY';
import type { Category } from '~/server_shared/swagger/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isUserImpersonated } from '~/UserUtils';
import { isFeatureEnabled, isProductionEnv, reportAxiosError, reportErrorInProductionOrThrow } from '~/Utils';

const DEFAULT_DEFAULT_SORT_ORDER = 'desc';

const TABLE_OPTION = {
  key: 'table',
  label: 'Table',
  value: 'table',
};

const BOARD_OPTION = {
  key: 'board',
  label: 'Board',
  value: 'board',
};

const VIEW_OPTIONS = [TABLE_OPTION, BOARD_OPTION];

interface NotificationsScreenProps {
  displayOptions: NotificationsTableDisplayOptions;
  disableSort?: boolean;
  actionComponent: ReactElement;
  onlyDismissed?: boolean;
  dismissedNavigationComponent?: ReactElement;
  useDefaultAssigneeFromPreferences?: boolean;
}

const NotificationsScreen: React.FC<NotificationsScreenProps> = ({
  displayOptions,
  disableSort,
  actionComponent,
  useDefaultAssigneeFromPreferences,
  onlyDismissed,
  dismissedNavigationComponent,
}) => {
  const { configuration, isLoading, isError } = useNotificationsPreferences();
  return (
    <LoadingSwitch isLoading={isLoading} isError={isError}>
      {configuration && (
        <NotificationsScreenInner
          displayOptions={displayOptions}
          disableSort={disableSort}
          configuration={configuration}
          actionComponent={actionComponent}
          useDefaultAssigneeFromPreferences={useDefaultAssigneeFromPreferences}
          onlyDismissed={onlyDismissed}
          dismissedNavigationComponent={dismissedNavigationComponent}
        />
      )}
    </LoadingSwitch>
  );
};

interface NotificationScreenInnerProps extends NotificationsScreenProps {
  configuration: NotificationsPreferences;
}

const BULK_OPERATIONS_ROUTES = {
  markAsRead: '/api/v1/notifications/bulk_mark_as_read',
  markAsUnread: '/api/v1/notifications/bulk_mark_as_unread',
  dismiss: '/api/v1/notifications/bulk_dismiss',
  undismiss: '/api/v1/notifications/bulk_undismiss',
};

const NotificationsScreenInner: React.FC<NotificationScreenInnerProps> = ({
  displayOptions,
  disableSort,
  configuration,
  actionComponent,
  useDefaultAssigneeFromPreferences,
  onlyDismissed,
  dismissedNavigationComponent,
}) => {
  const { user, userOrganization } = useCms();
  const [viewOption, setViewOption] = useState(TABLE_OPTION.value);
  const [sortByColumn, setSortByColumn] = React.useState({
    order:
      configuration.is_default_sort_order_enabled && configuration.default_sort_order
        ? configuration.default_sort_order
        : DEFAULT_DEFAULT_SORT_ORDER,
    id: 'due_date',
  });
  // Held apart from the page results: a claim reload refetches the list from page 1, which may no longer include it
  const [notificationToShow, setNotificationToShow] = useState<Pick<ClaimNotificationModel, 'id' | 'claim_id'>>();
  const rowsPerPageOptions = [configuration.default_notifications_per_page || 10];

  const defaultAssignee =
    useDefaultAssigneeFromPreferences && configuration.is_default_view_all_in_claims_page ? ['All'] : [user.id];

  const [filters, setFilters] = useState<NotificationsFiltersValues>({
    sub_organization_ids: [],
    categories_ids: [],
    statuses: [],
    types: [],
    priorities: [],
    start_due_date: null,
    end_due_date: null,
    adjusters_ids: defaultAssignee,
  });

  const [notificationIdToIsSelectedMap, setNotificationIdToIsSelectedMap] = useState<{
    [notification_id: number]: boolean;
  }>({});
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Local state for optimistic updates
  const [localNotificationUpdates, setLocalNotificationUpdates] = useState<{
    [id: number]: Partial<{
      priority: keyof typeof CLAIM_NOTIFICATIONS_PRIORITY;
      categories: Category[];
      due_date: string;
      datetime_dismissed: boolean;
    }>;
  }>({});
  const [localReadStatusUpdates, setLocalReadStatusUpdates] = useState<{ [id: string]: boolean }>({});

  const { claim: claimInContext, onAsyncClaimUpdate, reloadingUuid: claimReloadingUuid } = useClaim(); // might not be in claim context

  // Use the new pagination hook
  const {
    pagination,
    pageResults: notifications,
    pageMeta,
    isLoading,
    isError,
    reload,
    refreshPageData,
  } = useNotificationsPagination(
    {
      should_return_dismissed: !!onlyDismissed,
      should_return_future_reminders: true,
      sortByColumn: sortByColumn && `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`,
      withReadStatus: true,
      typeSubtypeFilters: filters.types
        ?.map((type) => (typeof type === 'string' && type !== 'All' ? { type, subtype: null } : undefined))
        .filter(Boolean) as { type: string; subtype: string | null }[],
      subOrganizationIds: filters.sub_organization_ids,
      categoriesIds: filters.categories_ids?.includes(0)
        ? [...filters.categories_ids.filter((id) => id !== 0), '0']
        : filters.categories_ids,
      statuses: filters.statuses,
      priorities: filters.priorities,
      startDueDateTime: filters.start_due_date ? localToServerDateTimeNoonUTC(filters.start_due_date) : null,
      endDueDateTime: filters.end_due_date ? localDateToEndOfDayUTC(filters.end_due_date) : null,
      adjustersIds: filters.adjusters_ids,
      claimId: claimInContext?.id,
      organizationId: userOrganization?.id,
    },
    configuration.is_default_notifications_per_page_enabled && configuration.default_notifications_per_page
      ? configuration.default_notifications_per_page
      : 10
  );

  // Destructure stable functions from pagination (for use in useEffect)
  const { setPage } = pagination;

  // Claim actions (e.g. Create Notification) refresh the claim in context; reload so their notifications show up
  const lastClaimReloadingUuid = useRef(claimReloadingUuid);
  useEffect(() => {
    if (lastClaimReloadingUuid.current === claimReloadingUuid) return;
    lastClaimReloadingUuid.current = claimReloadingUuid;
    reload();
  }, [claimReloadingUuid, reload]);

  // Derive state from the hook, merging with local optimistic updates
  // TODO: remove "as" once we model exposure with pydantic and change it in class NotificationsPageMeta(CoerciveBaseModel)
  const exposuresDict = useMemo(
    () => keyBy((pageMeta?.exposures as ExposureModel[]) || [], 'id'),
    [pageMeta?.exposures]
  );

  const notificationsReadStatusDict = useMemo(
    () => ({ ...(pageMeta?.notifications_read_status || {}), ...localReadStatusUpdates }),
    [pageMeta?.notifications_read_status, localReadStatusUpdates]
  );

  // Merge notifications with local updates for optimistic UI
  const notificationsWithLocalUpdates = useMemo(() => {
    return notifications.map((notification) => {
      const localUpdate = localNotificationUpdates[notification.id];
      if (!localUpdate) return notification;

      return {
        ...notification,
        ...localUpdate,
        datetime_dismissed:
          localUpdate.datetime_dismissed !== undefined
            ? localUpdate.datetime_dismissed
              ? serverDateToLocal(new Date())
              : undefined
            : notification.datetime_dismissed,
      };
    });
  }, [notifications, localNotificationUpdates]);

  const isNotificationKanbanBoardEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.NOTIFICATION_KANBAN_BOARD
  );

  const handleBulkOperating =
    (routeKey: keyof typeof BULK_OPERATIONS_ROUTES, shouldClearSelectionPostOperation?: boolean) => async () => {
      try {
        setIsBulkUpdating(true);

        await axios.post(BULK_OPERATIONS_ROUTES[routeKey], {
          notification_ids: Object.keys(notificationIdToIsSelectedMap),
        });
        await refreshPageData();

        if (shouldClearSelectionPostOperation) {
          setNotificationIdToIsSelectedMap({});
        }
        setIsBulkUpdating(false);
      } catch (err) {
        await reportAxiosError(err);
        setIsBulkUpdating(false);
      }
    };

  const bulkActions = [
    {
      key: 'undismiss',
      Icon: DismissIcon,
      onClickAction: handleBulkOperating('undismiss', true),
      buttonText: 'Bulk undismiss',
    },
    {
      key: 'dismiss',
      Icon: DismissIcon,
      onClickAction: handleBulkOperating('dismiss', true),
      buttonText: 'Bulk dismiss',
    },
    {
      key: 'mark_as_unread',
      Icon: MailIcon,
      onClickAction: handleBulkOperating('markAsUnread'),
      buttonText: 'Bulk mark as unread',
    },
    {
      key: 'mark_as_read',
      Icon: OpenMailIcon,
      onClickAction: handleBulkOperating('markAsRead'),
      buttonText: 'Bulk mark as read',
    },
  ];

  // Create the search handler without debounce first
  const handleSearchImmediate = useCallback(
    (values: NotificationsFiltersValues) => {
      setFilters(values);
      setPage(0);
      setNotificationIdToIsSelectedMap({});
    },
    [setFilters, setPage, setNotificationIdToIsSelectedMap]
  );

  // Create the debounced version using useMemo to avoid recreating on every render
  const onSearch = useMemo(() => debounce(handleSearchImmediate, 300), [handleSearchImmediate]);

  const handleSort = (e: React.MouseEvent<HTMLSpanElement>, { id, order }: { id: string; order: 'desc' | 'asc' }) => {
    setSortByColumn({ id, order });
    pagination.setPage(0);
  };
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    pagination.setRowsPerPage(Number(e.target.value));
  };
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    pagination.setPage(newPage);
  };

  const handleUpdateNotification = (
    notificationId: number,
    updates: Partial<{
      priority: keyof typeof CLAIM_NOTIFICATIONS_PRIORITY;
      categories: Category[];
      due_date: string;
      datetime_dismissed: boolean;
    }>
  ) => {
    // Optimistically update local state for immediate UI feedback
    setLocalNotificationUpdates((prev) => ({
      ...prev,
      [notificationId]: { ...prev[notificationId], ...updates },
    }));
  };

  const updateClaimIfInContext = () => {
    if (claimInContext && onAsyncClaimUpdate) {
      return onAsyncClaimUpdate();
    } else {
      return Promise.resolve();
    }
  };
  const handleUpdate = async () => {
    await updateClaimIfInContext();
  };

  const handleMarkAsUnread = (notificationId: number) => {
    setLocalReadStatusUpdates((prev) => ({ ...prev, [notificationId]: false }));
  };

  const handleMarkAsRead = (notificationId: number) => {
    setLocalReadStatusUpdates((prev) => ({ ...prev, [notificationId]: true }));
  };

  const handleShowNotification = async (notificationId: number) => {
    const notification = notificationsWithLocalUpdates.find((c) => c.id === notificationId);
    setNotificationToShow(notification && { id: notification.id, claim_id: notification.claim_id });

    try {
      if (notificationsReadStatusDict?.[notificationId]) return;
      if (isProductionEnv() && isUserImpersonated(user)) return;

      await axios.post(`/api/v1/claims/notifications/${notificationId}/mark_as_read`);
      handleMarkAsRead(notificationId);
    } catch (error) {
      reportErrorInProductionOrThrow(error);
    }
  };

  const handleRefreshNotifications = () => {
    reload();
  };

  const displayLoading = isLoading || isError;

  return (
    <CardDialog title={`${onlyDismissed ? 'Dismissed' : ''} Notifications`} action={actionComponent}>
      {!claimInContext && isNotificationKanbanBoardEnabled ? (
        <NotificationCountersView reloadingUuid={notificationToShow?.id.toString() ?? ''} />
      ) : null}
      {dismissedNavigationComponent ? dismissedNavigationComponent : null}
      <NotificationsFilters onSearch={onSearch} isUpdating={isLoading} defaultAssignee={defaultAssignee} />
      <div className="flex items-center justify-start space-x-24">
        {isNotificationKanbanBoardEnabled ? (
          <ToggleButtonGroup
            exclusive
            options={VIEW_OPTIONS}
            value={viewOption}
            onChange={(event, value) => {
              if (!value) return;
              setFilters({ ...filters, categories_ids: [] });
              setViewOption(value);
            }}
          />
        ) : null}
      </div>
      {!isEmpty(notificationIdToIsSelectedMap) && (
        <BulkSelectionPanel
          selectedCounter={Object.keys(notificationIdToIsSelectedMap).length}
          totalCounter={pagination.totalCount}
          onClearSelection={() => setNotificationIdToIsSelectedMap({})}
          actions={bulkActions?.filter(({ key }) => (onlyDismissed ? key !== 'dismiss' : key !== 'undismiss'))}
          disabled={isBulkUpdating}
        />
      )}
      {!displayLoading && viewOption === TABLE_OPTION.value ? (
        <NotificationsTable
          notifications={notificationsWithLocalUpdates}
          displayOptions={displayOptions}
          exposuresDict={exposuresDict}
          notificationsReadStatusDict={notificationsReadStatusDict}
          pagination={{
            rowsPerPage: pagination.rowsPerPage,
            page: pagination.page,
            rowsPerPageOptions,
            count: pagination.totalCount,
            onChangePage: handleChangePage,
            onChangeRowsPerPage: handleChangeRowsPerPage,
          }}
          onHandleShowNotification={handleShowNotification}
          onSortByColumn={handleSort}
          disableSort={disableSort}
          defaultSortOrder={sortByColumn.order}
          onlyDismissed={onlyDismissed}
          notificationIdToIsSelectedMap={notificationIdToIsSelectedMap}
          setNotificationIdToIsSelectedMap={setNotificationIdToIsSelectedMap}
          defaultSortColumn={sortByColumn.id}
          markOverdue={configuration?.is_overdue_date_in_red || false}
          counterViewText={
            isEmpty(notificationIdToIsSelectedMap) ? (
              <CounterWithRefresh
                onRefresh={handleRefreshNotifications}
                isLoading={isLoading}
                pagination={pagination}
                refreshTooltipText="Refresh notifications"
                refreshAriaLabel="Refresh notifications"
              />
            ) : undefined
          }
          onUpdateNotification={handleUpdateNotification}
        />
      ) : null}
      {!displayLoading && viewOption === BOARD_OPTION.value ? (
        <NotificationKanbanBoard
          notifications={notificationsWithLocalUpdates}
          exposuresDict={exposuresDict}
          filterCategoriesIds={filters.categories_ids}
          setCategories={(categories: number[]) => setFilters({ ...filters, categories_ids: categories })}
          onShowNotification={handleShowNotification}
          onUpdateNotification={handleUpdateNotification}
        />
      ) : null}
      {displayLoading ? <SkeletonTable rowsCount={4} columnsCount={10} isError={isError} /> : null}
      {notificationToShow ? (
        <ViewNotificationCard
          onClose={() => setNotificationToShow(undefined)}
          notification={notificationToShow}
          onUpdate={handleUpdate}
          onMarkAsUnread={handleMarkAsUnread}
          onUpdateNotification={handleUpdateNotification}
        />
      ) : null}
    </CardDialog>
  );
};

export default NotificationsScreen;
